import { useState } from "react";
import { CalendarIcon, Star, StarOff, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multiselect";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { formatDate, cn } from "@/lib/utils";
import { TransactionFilters, SavedFilterPreset, TransactionType, TransactionStatus } from "@/types/transaction";

// Mock customers for filter
const mockCustomers = [
  { value: "CUST001", label: "Acme Corporation" },
  { value: "CUST002", label: "Global Industries Ltd" },
  { value: "CUST003", label: "Tech Solutions Inc" },
  { value: "CUST004", label: "Premier Services Group" },
  { value: "CUST005", label: "Enterprise Systems Co" },
];

const transactionTypes: { value: TransactionType; label: string }[] = [
  { value: "invoice", label: "Invoice" },
  { value: "credit_note", label: "Credit Note" },
  { value: "receipt", label: "Receipt" },
  { value: "debit_adjustment", label: "Debit Adjustment" },
  { value: "credit_adjustment", label: "Credit Adjustment" },
];

const statuses: { value: TransactionStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "void", label: "Void" },
  { value: "draft", label: "Draft" },
  { value: "overdue", label: "Overdue" },
  { value: "partially_paid", label: "Partially Paid" },
];

interface FilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TransactionFilters;
  onFiltersChange: (filters: TransactionFilters) => void;
  savedPresets: SavedFilterPreset[];
  activePresetId: string | null;
  onApplyPreset: (preset: SavedFilterPreset) => void;
  onDeletePreset: (presetId: string) => void;
  onSavePreset: () => void;
  onClearAll: () => void;
}

export function FilterSheet({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  savedPresets,
  activePresetId,
  onApplyPreset,
  onDeletePreset,
  onSavePreset,
  onClearAll,
}: FilterSheetProps) {
  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg px-6">
        <SheetHeader>
          <SheetTitle>Filter Transactions</SheetTitle>
          <SheetDescription>
            Apply filters to narrow down your transaction list
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4 mt-6">
          <div className="space-y-6">
            {/* Saved Presets */}
            {savedPresets.length > 0 && (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Saved Presets</Label>
                <div className="space-y-2">
                  {savedPresets.map((preset) => (
                    <div
                      key={preset.id}
                      className="flex items-center gap-2 p-2 rounded-md border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <button
                        onClick={() => onApplyPreset(preset)}
                        className="flex items-center gap-2 flex-1 text-left"
                      >
                        {activePresetId === preset.id ? (
                          <Star className="h-4 w-4 fill-primary text-primary" />
                        ) : (
                          <StarOff className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="text-sm font-medium">{preset.name}</span>
                      </button>
                      {!preset.isDefault && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeletePreset(preset.id)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Separator />
              </div>
            )}

            {/* Customer Filter */}
            <div className="space-y-2">
              <Label>Customer</Label>
              <MultiSelect
                options={mockCustomers}
                placeholder="Select customers..."
                emptyMessage="No customers found."
                value={filters.customerIds}
                onValueChange={(value) => onFiltersChange({ ...filters, customerIds: value })}
              />
            </div>

            {/* Transaction Type Filter */}
            <div className="space-y-2">
              <Label>Transaction Type</Label>
              <MultiSelect
                options={transactionTypes}
                placeholder="Select transaction types..."
                emptyMessage="No transaction types found."
                value={filters.transactionTypes}
                onValueChange={(value) => onFiltersChange({ ...filters, transactionTypes: value as TransactionType[] })}
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <MultiSelect
                options={statuses}
                placeholder="Select statuses..."
                emptyMessage="No statuses found."
                value={filters.statuses}
                onValueChange={(value) => onFiltersChange({ ...filters, statuses: value as TransactionStatus[] })}
              />
            </div>

            <Separator />

            {/* Date Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Date Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date-from" className="text-sm">From</Label>
                  <Popover open={dateFromOpen} onOpenChange={setDateFromOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="date-from"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filters.dateFrom && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.dateFrom ? formatDate(filters.dateFrom) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateFrom ? new Date(filters.dateFrom) : undefined}
                        onSelect={(date) => {
                          onFiltersChange({
                            ...filters,
                            dateFrom: date ? date.toISOString().split("T")[0] : undefined,
                          });
                          setDateFromOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date-to" className="text-sm">To</Label>
                  <Popover open={dateToOpen} onOpenChange={setDateToOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="date-to"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filters.dateTo && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.dateTo ? formatDate(filters.dateTo) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateTo ? new Date(filters.dateTo) : undefined}
                        onSelect={(date) => {
                          onFiltersChange({
                            ...filters,
                            dateTo: date ? date.toISOString().split("T")[0] : undefined,
                          });
                          setDateToOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <Separator />

            {/* Amount Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Amount Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount-min" className="text-sm">Min Amount</Label>
                  <Input
                    id="amount-min"
                    type="number"
                    placeholder="0"
                    value={filters.amountMin ?? ""}
                    onChange={(e) =>
                      onFiltersChange({
                        ...filters,
                        amountMin: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount-max" className="text-sm">Max Amount</Label>
                  <Input
                    id="amount-max"
                    type="number"
                    placeholder="100000"
                    value={filters.amountMax ?? ""}
                    onChange={(e) =>
                      onFiltersChange({
                        ...filters,
                        amountMax: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Footer Actions */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-background border-t flex gap-2">
          <Button variant="outline" onClick={onClearAll} className="flex-1">
            Clear All
          </Button>
          <Button onClick={onSavePreset} className="flex-1">
            Save Preset
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
